import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { AuthService } from '../application/services/AuthService'
import { FirebaseAuthRepository } from '../infrastructure/repositories/FirebaseAuthRepository'
import type { AuthResult } from '../domain/repositories/IAuthRepository'
import { getAuth, onAuthStateChanged, sendEmailVerification } from 'firebase/auth'
import { FirebaseProvider } from '../infrastructure/providers/FirebaseProvider'
import { contentsquareService } from '../services/tracking/contentsquare'
import { useNotification } from '../composables/useNotification'
import type { InviteData } from '../domain/types/Company';
import type { CompanyRole, ModulePermissions } from '../domain/types/Role';
import { resolveUserRole, projectUserRoleForCompany } from '../domain/types/Role';

export const useAuthStore = defineStore('auth', () => {
  const authService = new AuthService(new FirebaseAuthRepository())
  const user = ref<AuthResult['user'] | null>(null)
  const isInitialized = ref(false)
  const isLoading = ref(true)
  const error = ref('')
  const { showNotification } = useNotification()
  const lastAuthCheck = ref<number>(Date.now())

  // Estado computado para roles y permisos
  // Rol efectivo: los usuarios heredados de solo vista siguen con role 'admin' y
  // clientType 'view' en Firestore; sin resolverlo quedaban exentos de la vista asignada.
  const userRole = computed(() => resolveUserRole(user.value?.role, user.value?.clientType) ?? 'viewer')
  const isAdmin = computed(() => userRole.value === 'admin')
  const isSuperAdmin = computed(() => (userRole.value as any) === 'superadmin')
  const isAdminOrSuperAdmin = computed(() => userRole.value === 'admin' || (userRole.value as any) === 'superadmin')
  /** Compañía con la que se proyecta el rol al cargar el usuario; el company store la corrige luego con applyCompanyRole. */
  const companyIdHint = (u: AuthResult['user'] | null | undefined) =>
    localStorage.getItem('selectedCompanyId') || u?.companyId || null

  /** Asigna el usuario proyectando su rol a la compañía activa (D11). */
  const assignUser = (u: AuthResult['user'] | null | undefined) => {
    user.value = u ? projectUserRoleForCompany(u, companyIdHint(u)) : null
  }

  /** Reproyecta el rol cuando cambia la compañía seleccionada. No reasigna si nada cambió (evita recargas del watch de usuario). */
  const applyCompanyRole = (companyId: string | null) => {
    if (!user.value) return
    const next = projectUserRoleForCompany(user.value, companyId)
    if (next.role !== user.value.role || next.clientType !== user.value.clientType) user.value = next
  }

  const canReverseApproval = computed(() => !!(user.value as any)?.canReverseApproval)
  const isSupportAdmin = computed(() => {
    if (!user.value) return false
    return (user.value as any)?.isSupportAdmin === true
  })




  // Función para actualizar atributos de usuario en Contentsquare
  const updateUserTracking = (userData: AuthResult['user'] | null) => {
    if (userData) {
      contentsquareService.setUserAttributes({
        userId: userData.uid,
        email: userData.email,
        role: userData.role,
        name: userData.name,
        lastName: userData.lastName
        // clientType: userData.clientType // ⚠️ Temporalmente comentado - no definido en UserAttributes
      })
    } else {
      contentsquareService.clearUserAttributes()
    }
  }

  /**
   * ¿Firebase tiene una sesión viva? Es distinto de tener el perfil hidratado:
   * el guard necesita separar "no autenticado" de "todavía no sé quién es".
   */
  const hasFirebaseSession = ref(false)

  /** Falló la hidratación del perfil pese a haber sesión (red, permisos). */
  const hydrationFailed = ref(false)

  // Resuelve cuando el único listener terminó su primer intento de hidratación.
  let resolveAuthReady: () => void
  const authReady = new Promise<void>(resolve => { resolveAuthReady = resolve })
  let authReadyResolved = false

  /**
   * Trae el perfil del usuario autenticado.
   *
   * Sin timeout a propósito: el `Promise.race` de 5 s que había aquí convertía
   * una red lenta en un cierre de sesión. En una pestaña nueva, sin conexión
   * caliente a Firestore, esos 5 s se alcanzan con facilidad y el guard veía
   * `user = null` con la sesión de Firebase intacta.
   */
  const hydrateUser = async () => {
    try {
      assignUser(await authService.getCurrentUser())
      updateUserTracking(user.value)
      hydrationFailed.value = false
      lastAuthCheck.value = Date.now()
    } catch (error: any) {
      // Un fallo de infraestructura no es un cierre de sesión. Cuando la
      // política de dominios rechaza, getCurrentUser cierra la sesión y
      // devuelve null: eso llega por la rama de arriba, no por aquí.
      console.error('❌ Error getting user data:', error)
      hydrationFailed.value = true
    }
  }

  // Inicializar el observer de estado de autenticación
  const initializeAuthState = () => {
    const auth = getAuth(FirebaseProvider.getInstance().getApp())

    onAuthStateChanged(auth, async (firebaseUser) => {
      isLoading.value = true
      hasFirebaseSession.value = !!firebaseUser

      try {
        if (firebaseUser) {
          await hydrateUser()
          // Un reintento: el fallo típico en pestaña fría es transitorio.
          if (hydrationFailed.value) await hydrateUser()

          if (hydrationFailed.value) {
            showNotification(
              'error',
              'Advertencia de Autenticación',
              'Problema temporal con la sesión. Continuando con datos locales...'
            )
          }
        } else {
          user.value = null
          updateUserTracking(null)
          hydrationFailed.value = false
        }
      } finally {
        isLoading.value = false
        isInitialized.value = true
        if (!authReadyResolved) {
          authReadyResolved = true
          resolveAuthReady()
        }
      }
    })
  }

  // Función para crear cuenta de usuario
  const createAccount = async (data: {
    email: string
    password: string
    name: string
    lastName: string
    hasCompletedOnboarding?: boolean
    companyId?: string | null
    role?: CompanyRole
    permissions?: ModulePermissions
    customRoleId?: string,
    inviteId?: string | null
  }) => {
    try {
      const result = await authService.createUser({
        email: data.email,
        password: data.password,
        name: data.name,
        lastName: data.lastName,
        hasCompletedOnboarding: data.hasCompletedOnboarding || false,
        companyId: data.companyId || null,
        role: data.role,
        permissions: data.permissions,
        customRoleId: data.customRoleId,
        inviteId: data.inviteId
      })

      assignUser(result.user)
      updateUserTracking(result.user)
      return result
    } catch (error: any) {
      console.error('Error en createAccount:', error)
      // Propagamos el error original
      throw error
    }
  }

  // Función para enviar correo electrónico de verificación
  const sendVerificationEmail = async () => {
    const auth = getAuth(FirebaseProvider.getInstance().getApp())
    if (auth.currentUser) {
      const actionCodeSettings = {
        url: `${window.location.origin}/trial/start`,
        handleCodeInApp: true
      }
      await sendEmailVerification(auth.currentUser, actionCodeSettings)
    } else {
      throw new Error('No hay usuario autenticado')
    }
  }

  // Función para verificar correo electrónico
  const checkEmailVerification = async () => {
    const auth = getAuth(FirebaseProvider.getInstance().getApp())
    if (auth.currentUser) {
      await auth.currentUser.reload()
      return auth.currentUser.emailVerified
    }
    return false
  }

  // Función para iniciar sesión
  const login = async (email: string, password: string) => {
    try {
      const result = await authService.login(email, password)
      assignUser(result.user)
      updateUserTracking(result.user)
      return { success: true, user: result.user }
    } catch (error: any) {
      console.error('Error logging in:', error)
      throw error
    }
  }

  // Función para iniciar sesión con proveedor
  const loginWithProvider = async (
    provider: 'google' | 'microsoft',
    inviteData?: InviteData
  ) => {
    try {
      const result = await authService.loginWithProvider(provider, inviteData)
      
      assignUser(result.user)
      updateUserTracking(user.value)
      
      return { success: true, user: result.user }
    } catch (error: any) {
      console.error('Error in provider login:', error)
      throw error
    }
  }

  // Función para cerrar sesión
  const logout = async () => {
    try {
      await authService.logout()
      user.value = null
      updateUserTracking(null)
    } catch (error: any) {
      console.error('Error logging out:', error)
      throw error
    }
  }

  /**
   * Verifica la autenticación para el guard del router.
   *
   * Antes registraba un SEGUNDO onAuthStateChanged que competía con el de
   * initializeAuthState por el mismo flag `isInitialized`: el primero en
   * terminar lo marcaba como resuelto aunque hubiera dejado `user` en null, y
   * a partir de ahí `checkAuth` retornaba de inmediato mandando al login.
   * Ahora hay un solo listener y esto solo espera su promesa.
   */
  const checkAuth = async (): Promise<boolean> => {
    await authReady

    // Auto-sanación: hay sesión de Firebase pero el perfil no se hidrató (red).
    // Reintentar en la siguiente navegación es más útil que expulsar.
    if (!user.value && hasFirebaseSession.value) {
      isLoading.value = true
      try {
        await hydrateUser()
      } finally {
        isLoading.value = false
      }
    }

    return user.value !== null
  }

  // Método para actualizar el usuario
  const setUser = (userData: AuthResult['user'] | null) => {
    assignUser(userData)
    updateUserTracking(userData)
  }

  // Método para actualizar datos del usuario
  const updateUser = async (userData: Partial<AuthResult['user']>) => {
    try {
      if (!user.value?.uid) {
        throw new Error('No hay usuario autenticado')
      }

      const updatedUser = await authService.updateUser(user.value.uid, userData)
      assignUser(updatedUser)
      updateUserTracking(updatedUser)
    } catch (error) {
      console.error('Error updating user:', error)
      throw error
    }
  }

  // Inicializar el observer de autenticación
  initializeAuthState()

  return {
    user,
    isInitialized,
    hasFirebaseSession,
    hydrationFailed,
    isLoading,
    error,
    userRole,
    isAdmin,
    isSuperAdmin,
    isAdminOrSuperAdmin,
    canReverseApproval,
    isSupportAdmin,
    applyCompanyRole,
    login,
    loginWithProvider,
    logout,
    createAccount,
    checkAuth,
    setUser,
    updateUser,
    sendVerificationEmail,
    checkEmailVerification,
    lastAuthCheck: computed(() => lastAuthCheck.value)
  }
})